import React, { useMemo, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import moment from "moment/moment";
import { handleRichTextOutput } from "./HandleRichTextOutput";
import RichTextEditor from "../RichTextEditor";
import "./ViewAndAddNewNotes.scss";

/**
 * ViewAndAddNewNotes — modern notes feed: a composer on top and a newest-first
 * list of note cards below. Each card shows the author (avatar + name), a
 * relative timestamp (exact on hover) with an "edited" tag, and a reliable
 * Copy-to-clipboard button. Same prop contract as before.
 *
 * @param {Array}    dataList         notes ({ Id, Description, Created/UpdatedStaffId, Created/UpdatedDateTime })
 * @param {Array}    staffData        staff list ({ Id, ImageUrl, FirstName, FullName })
 * @param {string}   description      current editor value (HTML)
 * @param {Function} setDescription   editor onChange
 * @param {Function} handleAddNewNotes save handler
 * @param {boolean}  isClearEditor
 * @param {Function} setIsClearEditor
 */
// Task 24800 - minutes as "2 h 14 m", the same shape the Time Entries tab
// uses, so the two screens read alike.
const formatDurationMins = (mins) => {
  const total = Number(mins);
  if (!Number.isFinite(total) || total <= 0) return null;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours && minutes) return `${hours} h ${minutes} m`;
  if (hours) return `${hours} h`;
  return `${minutes} m`;
};

// How long was logged with this note. DurationMins rather than the billable
// figure: the question being answered is "how much time was spent", not "how
// much of it is going on the invoice".
const timeLoggedOn = (note) => formatDurationMins(note?.JobTimeEntry_DurationMins);

export default function ViewAndAddNewNotes({
  dataList,
  staffData,
  description,
  setDescription,
  handleAddNewNotes,
  isClearEditor,
  setIsClearEditor,
  // Optional: opt-in "show to customer" toggle (e.g. for job notes surfaced in
  // the customer portal). Off/absent for all other note usages.
  showCustomerToggle = false,
  customerToggleValue = false,
  onCustomerToggleChange,
}) {
  const [copiedId, setCopiedId] = useState(null);
  const bodyRefs = useRef({});

  // Newest first (by updated, falling back to created).
  const notes = useMemo(() => {
    const list = Array.isArray(dataList) ? [...dataList] : [];
    return list.sort((a, b) => {
      const da = new Date(a?.UpdatedDateTime || a?.CreatedDateTime || 0);
      const db = new Date(b?.UpdatedDateTime || b?.CreatedDateTime || 0);
      return db - da;
    });
  }, [dataList]);

  const resolveAuthor = (note) => {
    const staffId = note?.UpdatedStaffId || note?.CreatedStaffId;
    const staff = staffData?.find((s) => s.Id === staffId);
    const name = staff?.FullName || staff?.FirstName || "Unknown";
    return {
      name,
      image: staff?.ImageUrl,
      initial: name?.charAt(0) || "?",
    };
  };

  const handleCopy = (note) => {
    const node = bodyRefs.current[note.Id];
    const text = node?.innerText?.trim();
    if (!text) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
    setCopiedId(note.Id);
    setTimeout(() => setCopiedId((id) => (id === note.Id ? null : id)), 1200);
  };

  const isEmptyHtml = (html) =>
    !html || html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() === "";

  return (
    <div className="notes-block">
      {/* Composer */}
      <div className="notes-composer">
        <h5 className="notes-composer__label">
          <i className="ri-quill-pen-line" />
          Add a note
        </h5>
        <RichTextEditor
          initialValue={description}
          placeholder={"Write a note…"}
          handleContentChange={(e) => setDescription(e)}
          isClearEditor={isClearEditor}
          setIsClearEditor={setIsClearEditor}
        />
        <div className="notes-composer__actions">
          {showCustomerToggle && (
            <label
              className="form-check-label"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: "auto", cursor: "pointer" }}
            >
              <input
                type="checkbox"
                className="form-check-input"
                style={{ margin: 0 }}
                checked={!!customerToggleValue}
                onChange={(e) => onCustomerToggleChange?.(e.target.checked)}
              />
              <span>Show to customer (visible in portal)</span>
            </label>
          )}
          <button
            type="button"
            className="notes-add-btn"
            onClick={handleAddNewNotes}
            disabled={isEmptyHtml(description)}
          >
            <i className="ri-add-line" />
            Add Note
          </button>
        </div>
      </div>

      {/* Feed */}
      {notes.length > 0 ? (
        <div className="notes-feed">
          {notes.map((note, index) => {
            const author = resolveAuthor(note);
            const stamp = note?.UpdatedDateTime || note?.CreatedDateTime;
            const isEdited =
              note?.UpdatedDateTime &&
              note?.CreatedDateTime &&
              note.UpdatedDateTime !== note.CreatedDateTime;
            const isCopied = copiedId === note.Id;

            return (
              <div className="note-card" key={note?.Id ?? index}>
                <div className="note-card__head">
                  <div className="note-card__avatar">
                    {author.image ? (
                      <img src={author.image} alt={author.name} />
                    ) : (
                      author.initial
                    )}
                  </div>
                  <div className="note-card__who">
                    <span className="note-card__name">{author.name}</span>
                    {/* Task 24801 - "a month ago" on its own is only ever an
                        approximation, and the exact stamp was hidden behind a
                        hover, which is no use on a touch screen and no use at
                        all when you are scanning a list trying to work out what
                        happened on which day. Both are shown now: the relative
                        one to read at a glance, the actual date and time beside
                        it to act on. The tooltip keeps the long form, which is
                        the only part that still earns a hover. */}
                    <Tooltip
                      title={
                        stamp
                          ? moment(stamp).format("dddd, D MMMM YYYY [at] HH:mm")
                          : ""
                      }
                      placement="top"
                      arrow
                    >
                      <span className="note-card__time">
                        <i className="ri-time-line" />
                        {stamp ? moment(stamp).fromNow() : "—"}
                        {stamp && (
                          <span className="note-card__time-exact">
                            {moment(stamp).format("DD/MM/YYYY HH:mm")}
                          </span>
                        )}
                        {isEdited && (
                          <span className="note-card__edited">edited</span>
                        )}
                      </span>
                    </Tooltip>
                    {Number(note?.FlagShowToCustomer) === 1 && (
                      <span
                        title={
                          note?.FlagPortalSourced
                            ? "Posted by the customer via the portal"
                            : "Visible to the customer in the portal"
                        }
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          marginTop: 2,
                          borderRadius: 999,
                          fontWeight: 700,
                          padding: "1px 8px",
                          fontSize: 11,
                          color: "#166534",
                          background: "rgba(34, 197, 94, 0.16)",
                          border: "1px solid rgba(34, 197, 94, 0.35)",
                          width: "fit-content",
                        }}
                      >
                        <i className={note?.FlagPortalSourced ? "ri-user-line" : "ri-eye-line"} />
                        {note?.FlagPortalSourced ? "From customer" : "Visible to customer"}
                      </span>
                    )}
                  </div>

                  {note?.Description && (
                    <button
                      type="button"
                      className={`note-card__copy${
                        isCopied ? " is-copied" : ""
                      }`}
                      onClick={() => handleCopy(note)}
                    >
                      <i
                        className={
                          isCopied ? "ri-check-line" : "ri-file-copy-line"
                        }
                      />
                      {isCopied ? "Copied" : "Copy"}
                    </button>
                  )}
                </div>

                <div
                  className="note-card__body"
                  ref={(el) => {
                    bodyRefs.current[note.Id] = el;
                  }}
                >
                  {handleRichTextOutput(note?.Description)}
                </div>

                {/* Task 24800 - the time logged with this note.
                    A timer stop writes the note and the time entry together and
                    they already share a TimeEntry_Id, but nothing showed it, so
                    "install PC" and "1.5 hr" read as two unrelated facts and
                    somebody had to match them up by eye against the Time
                    Entries tab. Only rendered for a note that came from a timer
                    stop; a typed note has no time and shows nothing. */}
                {note?.JobTimeEntry_Id && timeLoggedOn(note) && (
                  <div className="note-card__time-entry">
                    <i className="ri-timer-line" />
                    <strong>{timeLoggedOn(note)}</strong>
                    {note?.JobTimeEntry_Product_Name && (
                      <span className="note-card__time-entry-product">
                        {note.JobTimeEntry_Product_Name}
                      </span>
                    )}
                    {note?.JobTimeEntry_StartDateTime && (
                      <span className="note-card__time-entry-when">
                        {moment(note.JobTimeEntry_StartDateTime).format(
                          "DD/MM/YYYY HH:mm"
                        )}
                        {note?.JobTimeEntry_EndDateTime
                          ? ` - ${moment(note.JobTimeEntry_EndDateTime).format(
                              "HH:mm"
                            )}`
                          : ""}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="notes-empty">
          <i className="ri-sticky-note-line" />
          <span>No notes yet — add the first one above</span>
        </div>
      )}
    </div>
  );
}
